import { useMemo, useState, type FormEvent } from 'react';
import { heroStats, hotRanking, latestScoops } from '@/mocks/home';

interface SearchItem {
  title: string;
  tag: string;
  href: string;
}

function Hero() {
  const [query, setQuery] = useState('');
  const [submitted, setSubmitted] = useState('');

  const searchPool = useMemo<SearchItem[]>(() => {
    const fromRanking = hotRanking.map((item) => ({
      title: item.title,
      tag: item.tag,
      href: '#hot',
    }));
    const fromLatest = latestScoops.map((item) => ({
      title: item.title,
      tag: item.category,
      href: '#latest',
    }));
    return [...fromRanking, ...fromLatest];
  }, []);

  const results = useMemo(() => {
    const keyword = submitted.trim();
    if (!keyword) {
      return [];
    }
    return searchPool
      .filter((item) => item.title.includes(keyword) || item.tag.includes(keyword))
      .slice(0, 4);
  }, [submitted, searchPool]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitted(query);
  };

  return (
    <section id="top" className="relative w-full min-h-[640px] md:min-h-[760px] flex items-center">
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://readdy.ai/api/search-image?query=Dark%20moody%20cinematic%20editorial%20background%20with%20scattered%20confidential%20documents%2C%20torn%20paper%20and%20ink%20stains%20on%20a%20deep%20charcoal%20surface%2C%20dramatic%20warm%20amber%20and%20vermillion%20red%20side%20lighting%2C%20investigative%20journalism%20atmosphere%2C%20high%20contrast%20and%20enigmatic&width=1600&height=1000&seq=nhbl-hero-01&orientation=landscape"
          alt="网红爆料 首屏主视觉：散落的机密文件与档案，深色调查氛围"
          title="网红爆料 行业黑幕与签约内幕追踪"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background-950/85 via-background-950/70 to-background-950/90"></div>
      </div>

      <div className="relative z-10 w-full max-w-shell mx-auto px-4 md:px-8 py-28 md:py-36">
        <div className="flex flex-col items-center text-center">
          <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-background-100/25 bg-background-950/40 text-xs text-background-100">
            <span className="w-1.5 h-1.5 rounded-full bg-primary-400"></span>
            全网追踪 · 每日更新
          </span>

          <h1 className="mt-7 font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-background-50 tracking-tight leading-tight">
            网红爆料
          </h1>

          <p className="mt-5 text-base md:text-xl text-background-100/90 font-label tracking-wide">
            深挖行业黑幕 · 还原签约内幕 · 曝光私聊记录
          </p>

          <p className="mt-5 max-w-2xl text-sm md:text-base leading-relaxed text-background-200/85">
            从潜规则到公司压榨，从私下聊天记录到合同陷阱，
            我们把散落在各处的线索整理成可核对的时间线，让每一条签约内幕都有出处。
          </p>

          <form
            onSubmit={handleSubmit}
            className="mt-9 w-full max-w-2xl flex flex-col sm:flex-row gap-3"
          >
            <div className="relative flex-1">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-lg"></i>
              </span>
              <input
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="搜索你关注的网红、公司或事件"
                className="w-full pl-11 pr-4 py-3.5 rounded-md bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:ring-2 focus:ring-primary-400 font-label"
              />
            </div>
            <button
              type="submit"
              className="whitespace-nowrap px-7 py-3.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              检索线索
            </button>
          </form>

          {submitted.trim() && (
            <div className="mt-4 w-full max-w-2xl rounded-md border border-background-100/20 bg-background-950/70 p-4 text-left">
              {results.length > 0 ? (
                <>
                  <p className="text-xs text-background-300 mb-3">
                    与「{submitted.trim()}」相关的线索 {results.length} 条
                  </p>
                  <ul className="flex flex-col gap-2">
                    {results.map((item) => (
                      <li key={item.title}>
                        <a
                          href={item.href}
                          className="flex items-start gap-2 text-sm text-background-100 hover:text-primary-300 transition-colors cursor-pointer"
                        >
                          <i className="ri-arrow-right-s-line mt-0.5 text-primary-300"></i>
                          <span className="line-clamp-1">{item.title}</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="text-xs text-background-300">
                  暂未匹配到相关线索，可尝试「签约」「压榨」「分成」等关键词。
                </p>
              )}
            </div>
          )}

          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-12 w-full max-w-3xl">
            {heroStats.map((stat) => (
              <div key={stat.label} className="flex flex-col items-center">
                <span className="font-heading text-2xl md:text-3xl font-bold text-accent-400">
                  {stat.value}
                </span>
                <span className="mt-1.5 text-xs text-background-300">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default Hero;